import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getContacts, 
  createContact, 
  updateContact, 
  deleteContact,
  bulkDeleteContacts,
  toggleFavori,
  bulkToggleFavori,
  exportContactsCSV,
  importContactsCSV
} from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ArrowLeft, Plus, Search, Building2, Mail, Phone, Pencil, Trash2, X,
  Users, Briefcase, Download, Upload, HardHat, Landmark, UserCircle, 
  CircleHelp, MapPin, Wrench, Filter, ChevronDown, Loader2, CheckSquare, Square,
  Star
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'all', label: 'Tous', icon: Users, color: 'bg-slate-600', activeColor: 'bg-blue-600 ring-2 ring-blue-400', textColor: 'text-blue-300' },
  { value: 'client', label: 'Clients', icon: Building2, color: 'bg-emerald-700/40', activeColor: 'bg-emerald-600 ring-2 ring-emerald-400', textColor: 'text-emerald-300', badge: 'bg-emerald-900 text-emerald-300' },
  { value: 'artisan', label: 'Artisans', icon: HardHat, color: 'bg-amber-700/40', activeColor: 'bg-amber-600 ring-2 ring-amber-400', textColor: 'text-amber-300', badge: 'bg-amber-900 text-amber-300' },
  { value: 'fournisseur', label: 'Fournisseurs', icon: Briefcase, color: 'bg-orange-700/40', activeColor: 'bg-orange-600 ring-2 ring-orange-400', textColor: 'text-orange-300', badge: 'bg-orange-900 text-orange-300' },
  { value: 'organisme', label: 'Organisme / Mairie', icon: Landmark, color: 'bg-violet-700/40', activeColor: 'bg-violet-600 ring-2 ring-violet-400', textColor: 'text-violet-300', badge: 'bg-violet-900 text-violet-300' },
  { value: 'personnel', label: 'Personnel', icon: UserCircle, color: 'bg-sky-700/40', activeColor: 'bg-sky-600 ring-2 ring-sky-400', textColor: 'text-sky-300', badge: 'bg-sky-900 text-sky-300' },
  { value: 'autre', label: 'Autre', icon: CircleHelp, color: 'bg-slate-700/40', activeColor: 'bg-slate-500 ring-2 ring-slate-400', textColor: 'text-slate-300', badge: 'bg-slate-700 text-slate-300' },
];

const CORPS_METIER_OPTIONS = [
  'Electricien', 'Plombier', 'Maçon', 'Peintre', 'Carreleur', 'Menuisier',
  'Plaquiste', 'Solier', 'Serrurier', 'Vitrier', 'Chauffagiste', 'Climaticien',
  'Charpentier', 'Couvreur', 'Façadier', 'Terrassier', 'Démolisseur',
  'Étancheur', 'Ferronnier', 'Métallier', 'Enseigniste', 'Autre'
];

const REGIONS_FRANCE = [
  'Auvergne-Rhône-Alpes', 'Bourgogne-Franche-Comté', 'Bretagne',
  'Centre-Val de Loire', 'Corse', 'Grand Est', 'Hauts-de-France',
  'Île-de-France', 'Normandie', 'Nouvelle-Aquitaine', 'Occitanie',
  'Pays de la Loire', "Provence-Alpes-Côte d'Azur"
];

const getCategoryConfig = (type) => CATEGORIES.find(c => c.value === type) || CATEGORIES[6];

const ContactCard = ({ contact, onEdit, onDelete, onToggleFavori, selectionMode, isSelected, onToggleSelect }) => {
  const cat = getCategoryConfig(contact.type_contact);
  const Icon = cat.icon;
  
  return (
    <div 
      className={`bg-slate-800/80 rounded-lg p-4 flex items-center justify-between hover:bg-slate-700/80 transition-all border ${isSelected ? 'border-red-500/70 bg-red-900/20' : 'border-slate-700/50'}`}
      data-testid={`contact-card-${contact.id}`}
      onClick={selectionMode ? () => onToggleSelect(contact.id) : undefined}
      style={selectionMode ? { cursor: 'pointer' } : {}}
    >
      <div className="flex items-center gap-4 min-w-0 flex-1">
        {selectionMode ? (
          <div className="flex-shrink-0" data-testid={`checkbox-${contact.id}`}>
            {isSelected 
              ? <CheckSquare className="w-5 h-5 text-red-400" />
              : <Square className="w-5 h-5 text-slate-500" />
            }
          </div>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleFavori(contact); }}
            className="flex-shrink-0 focus:outline-none transition-transform hover:scale-110"
            data-testid={`favori-${contact.id}`}
          >
            <Star className={`w-5 h-5 ${contact.favori ? 'fill-yellow-400 text-yellow-400' : 'text-slate-600 hover:text-yellow-400'}`} />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-white truncate">
              {contact.prenom} {contact.nom}
            </h3>
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${cat.badge || 'bg-slate-700 text-slate-300'}`}>
              {cat.label}
            </span>
            {contact.corps_metier && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-900/60 text-amber-200 flex items-center gap-1">
                <Wrench className="w-3 h-3" />{contact.corps_metier}
              </span>
            )}
            {contact.region && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-900/60 text-blue-200 flex items-center gap-1">
                <MapPin className="w-3 h-3" />{contact.region}
              </span>
            )}
            {contact.metier && !contact.corps_metier && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-700 text-slate-300">
                {contact.metier}
              </span>
            )}
          </div>
          {contact.entreprise && (
            <p className="text-slate-400 text-sm flex items-center gap-1 truncate">
              <Building2 className="w-3 h-3 flex-shrink-0" />
              {contact.entreprise}
            </p>
          )}
          <div className="flex items-center gap-4 mt-0.5 text-sm text-slate-400 flex-wrap">
            {contact.email && (
              <a href={`mailto:${contact.email}`} className="flex items-center gap-1 hover:text-blue-300 transition-colors truncate">
                <Mail className="w-3 h-3 flex-shrink-0" />{contact.email}
              </a>
            )}
            {contact.telephone && (
              <a href={`tel:${contact.telephone}`} className="flex items-center gap-1 hover:text-green-300 transition-colors">
                <Phone className="w-3 h-3 flex-shrink-0" />{contact.telephone}
              </a>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
        <Button variant="ghost" size="sm" onClick={() => onEdit(contact)} className="text-slate-400 hover:text-white h-8 w-8 p-0" data-testid={`edit-contact-${contact.id}`}>
          <Pencil className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete(contact)} className="text-slate-400 hover:text-red-400 h-8 w-8 p-0" data-testid={`delete-contact-${contact.id}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const ContactsPage = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [corpsMetierFilter, setCorpsMetierFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [favoriCount, setFavoriCount] = useState(0);
  const [showFavoris, setShowFavoris] = useState(false);
  const [formData, setFormData] = useState({
    nom: '', prenom: '', entreprise: '', email: '', telephone: '',
    type_contact: 'client', metier: '', corps_metier: '', region: '', notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showImportPreview, setShowImportPreview] = useState(false);
  const [importPreviewData, setImportPreviewData] = useState([]);
  const [importFile, setImportFile] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const fileInputRef = useRef(null);

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const typeFilter = filter === 'all' ? null : filter;
      const favoriFilter = showFavoris ? true : null;
      const cmFilter = filter === 'artisan' && corpsMetierFilter ? corpsMetierFilter : null;
      const regFilter = filter === 'artisan' && regionFilter ? regionFilter : null;
      const res = await getContacts(typeFilter, searchTerm, page, 50, favoriFilter, cmFilter, regFilter);
      const data = res.data;
      setContacts(data.contacts || []);
      setTotalPages(data.pages || 1);
      setTotalCount(data.total || 0);
      setFavoriCount(data.favori_count || 0);
      setAvailableCorpsMetier(data.distinct_corps_metier || []);
      setAvailableRegions(data.distinct_regions || []);
      const counts = data.counts || {};
      const allTotal = Object.values(counts).reduce((a, b) => a + b, 0);
      setCategoryCounts({ all: allTotal, ...counts });
    } catch (error) {
      console.error('Erreur chargement contacts:', error);
    } finally {
      setLoading(false);
    }
  }, [filter, searchTerm, page, showFavoris, corpsMetierFilter, regionFilter]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  // Sub-filter states (populated from server)
  const [availableCorpsMetier, setAvailableCorpsMetier] = useState([]);
  const [availableRegions, setAvailableRegions] = useState([]);

  // No local sub-filtering needed - server handles it
  const filteredContacts = contacts;

  const handleExportCSV = async () => {
    try {
      const response = await exportContactsCSV();
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contacts_maag_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Contacts exportés avec succès');
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImportFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      const preview = lines.slice(0, 11).map(line => {
        // Try semicolon first, then comma
        const parts = line.includes(';') ? line.split(';') : line.split(',');
        return parts.map(p => p.trim().replace(/^"|"$/g, ''));
      });
      setImportPreviewData(preview);
      setShowImportPreview(true);
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleConfirmImport = async () => {
    if (!importFile) return;
    try {
      setImporting(true);
      const fd = new FormData();
      fd.append('file', importFile);
      const response = await importContactsCSV(fd);
      if (response.data.success) {
        toast.success(`${response.data.imported} contacts importés`);
        if (response.data.skipped > 0) toast.info(`${response.data.skipped} lignes ignorées`);
        if (response.data.errors?.length > 0) toast.warning(`${response.data.errors.length} erreurs`);
        fetchContacts();
      }
    } catch (error) {
      toast.error("Erreur lors de l'import");
    } finally {
      setImporting(false);
      setShowImportPreview(false);
      setImportFile(null);
      setImportPreviewData([]);
    }
  };

  const handleOpenModal = (contact = null) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        nom: contact.nom || '', prenom: contact.prenom || '',
        entreprise: contact.entreprise || '', email: contact.email || '',
        telephone: contact.telephone || '', type_contact: contact.type_contact || 'client',
        metier: contact.metier || '', corps_metier: contact.corps_metier || '',
        region: contact.region || '', notes: contact.notes || ''
      });
    } else {
      setEditingContact(null);
      setFormData({
        nom: '', prenom: '', entreprise: '', email: '', telephone: '',
        type_contact: filter !== 'all' ? filter : 'client',
        metier: '', corps_metier: '', region: '', notes: ''
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.nom.trim()) { toast.error('Le nom est obligatoire'); return; }
    const isEdit = !!editingContact;
    try {
      setSaving(true);
      if (isEdit) {
        await updateContact(editingContact.id, formData);
      } else {
        await createContact(formData);
      }
      toast.success(isEdit ? 'Contact modifié' : 'Contact créé');
      setShowModal(false);
      setEditingContact(null);
      fetchContacts();
    } catch (error) {
      console.error('Save error:', error);
      const msg = error?.response?.data?.detail || error?.message || 'sauvegarde impossible';
      toast.error('Erreur : ' + msg);
    } finally { setSaving(false); }
  };

  const handleDelete = async (contact) => {
    if (!window.confirm(`Supprimer le contact "${contact.prenom} ${contact.nom}" ?`)) return;
    try {
      await deleteContact(contact.id);
      fetchContacts();
      toast.success('Contact supprimé');
    } catch (error) { toast.error('Erreur lors de la suppression'); }
  };

  const handleToggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredContacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredContacts.map(c => c.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Supprimer ${selectedIds.size} contact(s) ?`)) return;
    try {
      setBulkDeleting(true);
      const allIds = [...selectedIds];
      const batchSize = 50;
      let totalDeleted = 0;
      
      for (let i = 0; i < allIds.length; i += batchSize) {
        const batch = allIds.slice(i, i + batchSize);
        const res = await bulkDeleteContacts(batch);
        totalDeleted += res.data.deleted || 0;
      }
      
      toast.success(`${totalDeleted} contacts supprimés`);
      setSelectedIds(new Set());
      setSelectionMode(false);
      fetchContacts();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Erreur lors de la suppression, réessayez');
      fetchContacts();
    } finally { setBulkDeleting(false); }
  };

  const handleExitSelection = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const handleToggleFavori = async (contact) => {
    try {
      await toggleFavori(contact.id);
      // Update locally for instant feedback
      setContacts(prev => prev.map(c => c.id === contact.id ? { ...c, favori: !c.favori } : c));
      setFavoriCount(prev => contact.favori ? prev - 1 : prev + 1);
      toast.success(contact.favori ? 'Retiré des favoris' : 'Ajouté aux favoris');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleBulkFavori = async (favori) => {
    if (selectedIds.size === 0) return;
    try {
      setBulkDeleting(true);
      const allIds = [...selectedIds];
      const batchSize = 50;
      for (let i = 0; i < allIds.length; i += batchSize) {
        await bulkToggleFavori(allIds.slice(i, i + batchSize), favori);
      }
      toast.success(`${selectedIds.size} contacts ${favori ? 'ajoutés aux' : 'retirés des'} favoris`);
      setSelectedIds(new Set());
      setSelectionMode(false);
      fetchContacts();
    } catch (error) {
      toast.error('Erreur');
    } finally { setBulkDeleting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="text-slate-500 hover:text-slate-800 transition-colors" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                Carnet de Contacts
              </h1>
              <p className="text-sm text-slate-500">{totalCount} contacts</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleExportCSV} className="text-slate-600" data-testid="export-contacts-btn">
              <Download className="w-4 h-4 mr-1.5" />Export
            </Button>
            <input type="file" ref={fileInputRef} accept=".csv" onChange={handleFileSelect} className="hidden" data-testid="import-contacts-input" />
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={importing} className="text-slate-600" data-testid="import-contacts-btn">
              <Upload className="w-4 h-4 mr-1.5" />{importing ? 'Import...' : 'Import CSV'}
            </Button>
            <Button variant={selectionMode ? "default" : "outline"} size="sm"
              onClick={() => selectionMode ? handleExitSelection() : setSelectionMode(true)}
              className={selectionMode ? "bg-red-600 hover:bg-red-700 text-white" : "text-slate-600"}
              data-testid="selection-mode-btn">
              <CheckSquare className="w-4 h-4 mr-1.5" />{selectionMode ? 'Annuler' : 'Sélection'}
            </Button>
            <Button size="sm" onClick={() => handleOpenModal()} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="add-contact-btn">
              <Plus className="w-4 h-4 mr-1.5" />Nouveau
            </Button>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-[1400px] mx-auto">
        {/* Category Filters */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {/* Favoris tab */}
          <button
            onClick={() => { setShowFavoris(!showFavoris); setPage(1); setSelectedIds(new Set()); }}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
              showFavoris
                ? 'bg-yellow-500 text-white shadow-md'
                : 'bg-white text-slate-600 hover:bg-yellow-50 border border-yellow-300'
            }`}
            data-testid="filter-favoris"
          >
            <Star className={`w-4 h-4 ${showFavoris ? 'fill-white text-white' : 'text-yellow-500'}`} />
            Favoris
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${showFavoris ? 'bg-white/20 text-white' : 'bg-yellow-100 text-yellow-700'}`}>
              {favoriCount}
            </span>
          </button>
          <div className="w-px bg-slate-200 mx-1" />
          {CATEGORIES.map(cat => {
            const Icon = cat.icon;
            const isActive = filter === cat.value;
            const count = categoryCounts[cat.value] || 0;
            return (
              <button
                key={cat.value}
                onClick={() => { setFilter(cat.value); setCorpsMetierFilter(''); setRegionFilter(''); setPage(1); setSelectedIds(new Set()); }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-slate-800 text-white shadow-md'
                    : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                }`}
                data-testid={`filter-${cat.value}`}
              >
                <Icon className={`w-4 h-4 ${isActive ? cat.textColor : 'text-slate-400'}`} />
                {cat.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Sub-filters for Artisan */}
        {filter === 'artisan' && (availableCorpsMetier.length > 0 || availableRegions.length > 0) && (
          <div className="flex gap-3 mb-4 items-center flex-wrap bg-amber-50 p-3 rounded-lg border border-amber-200">
            <Filter className="w-4 h-4 text-amber-600" />
            {availableCorpsMetier.length > 0 && (
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-amber-700">Corps de métier :</label>
                <select
                  value={corpsMetierFilter}
                  onChange={(e) => { setCorpsMetierFilter(e.target.value); setPage(1); }}
                  className="text-sm border border-amber-300 rounded-md px-2 py-1 bg-white text-slate-700 focus:ring-2 focus:ring-amber-400 focus:outline-none"
                  data-testid="filter-corps-metier"
                >
                  <option value="">Tous</option>
                  {availableCorpsMetier.map(cm => <option key={cm} value={cm}>{cm}</option>)}
                </select>
              </div>
            )}
            {availableRegions.length > 0 && (
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-amber-700">Région :</label>
                <select
                  value={regionFilter}
                  onChange={(e) => { setRegionFilter(e.target.value); setPage(1); }}
                  className="text-sm border border-amber-300 rounded-md px-2 py-1 bg-white text-slate-700 focus:ring-2 focus:ring-amber-400 focus:outline-none"
                  data-testid="filter-region"
                >
                  <option value="">Toutes</option>
                  {availableRegions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            )}
            {(corpsMetierFilter || regionFilter) && (
              <button onClick={() => { setCorpsMetierFilter(''); setRegionFilter(''); }} className="text-xs text-amber-600 hover:text-amber-800 underline">
                Réinitialiser
              </button>
            )}
          </div>
        )}

        {/* Search */}
        <div className="mb-5">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher par nom, entreprise, email, tél..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              className="pl-10 bg-white border-slate-200 text-slate-800"
              data-testid="search-contacts"
            />
          </div>
        </div>

        {/* Bulk Action Bar */}
        {selectionMode && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={handleSelectAll} className="flex items-center gap-2 text-sm font-medium text-red-700 hover:text-red-900" data-testid="select-all-btn">
                {selectedIds.size === filteredContacts.length && filteredContacts.length > 0
                  ? <CheckSquare className="w-4 h-4" />
                  : <Square className="w-4 h-4" />
                }
                {selectedIds.size === filteredContacts.length && filteredContacts.length > 0 ? 'Tout désélectionner' : 'Tout sélectionner'}
              </button>
              <span className="text-sm text-red-600">
                {selectedIds.size > 0 ? `${selectedIds.size} contact(s) sélectionné(s)` : 'Cliquez sur les contacts à supprimer'}
              </span>
            </div>
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => handleBulkFavori(true)} disabled={bulkDeleting}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white" data-testid="bulk-favori-btn">
                  <Star className="w-4 h-4 mr-1.5 fill-white" />Favoris ({selectedIds.size})
                </Button>
                <Button size="sm" variant="destructive" onClick={handleBulkDelete} disabled={bulkDeleting}
                  className="bg-red-600 hover:bg-red-700" data-testid="bulk-delete-btn">
                  {bulkDeleting ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />En cours...</> : <><Trash2 className="w-4 h-4 mr-1.5" />Supprimer ({selectedIds.size})</>}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Contacts List */}
        {loading ? (
          <div className="text-center py-12 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Chargement...</div>
        ) : filteredContacts.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
            <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 mb-4">Aucun contact trouvé</p>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => handleOpenModal()} className="bg-indigo-600 hover:bg-indigo-700 text-white" size="sm">
                <Plus className="w-4 h-4 mr-1.5" />Ajouter un contact
              </Button>
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                <Upload className="w-4 h-4 mr-1.5" />Importer CSV
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid gap-2">
            {filteredContacts.map((contact) => (
              <ContactCard key={contact.id} contact={contact} onEdit={handleOpenModal} onDelete={handleDelete}
                onToggleFavori={handleToggleFavori}
                selectionMode={selectionMode} isSelected={selectedIds.has(contact.id)} onToggleSelect={handleToggleSelect} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && !loading && (
          <div className="flex items-center justify-center gap-2 mt-6 pb-6">
            <Button variant="outline" size="sm" disabled={page <= 1}
              onClick={() => { setPage(p => p - 1); setSelectedIds(new Set()); window.scrollTo(0, 0); }}
              className="text-slate-600" data-testid="prev-page-btn">
              Précédent
            </Button>
            <span className="text-sm text-slate-600 px-3">
              Page {page} / {totalPages}
            </span>
            <Button variant="outline" size="sm" disabled={page >= totalPages}
              onClick={() => { setPage(p => p + 1); setSelectedIds(new Set()); window.scrollTo(0, 0); }}
              className="text-slate-600" data-testid="next-page-btn">
              Suivant
            </Button>
          </div>
        )}
      </div>

      {/* Modal Ajout/Modification */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white sm:rounded-xl w-full sm:max-w-lg h-full sm:h-auto sm:max-h-[90vh] shadow-2xl flex flex-col">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-bold text-slate-800">
                {editingContact ? 'Modifier le contact' : 'Nouveau contact'}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-indigo-600 active:bg-indigo-800 text-white h-10 px-5 text-sm font-semibold rounded-lg sm:hidden disabled:opacity-50 touch-manipulation"
                  data-testid="save-contact-btn-top"
                >
                  {saving ? 'Chargement...' : (editingContact ? 'Modifier' : 'Créer')}
                </button>
                <button type="button" onClick={() => { setShowModal(false); setEditingContact(null); setSaving(false); }} className="text-slate-400 hover:text-slate-600 p-1">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              {/* Category selector */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Catégorie</label>
                <div className="grid grid-cols-3 gap-2">
                  {CATEGORIES.filter(c => c.value !== 'all').map(cat => {
                    const Icon = cat.icon;
                    const isActive = formData.type_contact === cat.value;
                    return (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, type_contact: cat.value })}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${
                          isActive ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                        }`}
                        data-testid={`type-${cat.value}`}
                      >
                        <Icon className={`w-3.5 h-3.5 ${isActive ? cat.textColor : 'text-slate-400'}`} />
                        {cat.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Nom & Prénom */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Nom *</label>
                  <Input value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    placeholder="Dupont" className="h-9 text-sm" data-testid="input-nom" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Prénom</label>
                  <Input value={formData.prenom} onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                    placeholder="Jean" className="h-9 text-sm" data-testid="input-prenom" />
                </div>
              </div>

              {/* Entreprise */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Entreprise</label>
                <Input value={formData.entreprise} onChange={(e) => setFormData({ ...formData, entreprise: e.target.value })}
                  placeholder="Entreprise SARL" className="h-9 text-sm" data-testid="input-entreprise" />
              </div>

              {/* Email & Téléphone */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="contact@exemple.fr" className="h-9 text-sm" data-testid="input-email" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Téléphone</label>
                  <Input value={formData.telephone} onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    placeholder="06 12 34 56 78" className="h-9 text-sm" data-testid="input-telephone" />
                </div>
              </div>

              {/* Artisan-specific fields */}
              {formData.type_contact === 'artisan' && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 space-y-3">
                  <p className="text-xs font-semibold text-amber-700 flex items-center gap-1"><HardHat className="w-3.5 h-3.5" />Infos Artisan</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-amber-700 mb-1">Corps de métier</label>
                      <select
                        value={formData.corps_metier}
                        onChange={(e) => setFormData({ ...formData, corps_metier: e.target.value })}
                        className="w-full h-9 px-2 text-sm border border-amber-300 rounded-md bg-white text-slate-700 focus:ring-2 focus:ring-amber-400 focus:outline-none"
                        data-testid="input-corps-metier"
                      >
                        <option value="">-- Choisir --</option>
                        {CORPS_METIER_OPTIONS.map(cm => <option key={cm} value={cm}>{cm}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-amber-700 mb-1">Région</label>
                      <select
                        value={formData.region}
                        onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                        className="w-full h-9 px-2 text-sm border border-amber-300 rounded-md bg-white text-slate-700 focus:ring-2 focus:ring-amber-400 focus:outline-none"
                        data-testid="input-region"
                      >
                        <option value="">-- Choisir --</option>
                        {REGIONS_FRANCE.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Métier/Spécialité for fournisseur */}
              {(formData.type_contact === 'fournisseur' || formData.type_contact === 'organisme') && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Spécialité / Fonction</label>
                  <Input value={formData.metier} onChange={(e) => setFormData({ ...formData, metier: e.target.value })}
                    placeholder={formData.type_contact === 'organisme' ? 'Bureau de contrôle, Mairie, DRAC...' : 'Electricité, Plomberie...'}
                    className="h-9 text-sm" data-testid="input-metier" />
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Informations complémentaires..." rows={2}
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-md text-slate-800 resize-none focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  data-testid="input-notes" />
              </div>
            </div>

            <div className="p-5 pb-8 border-t border-slate-200 hidden sm:flex justify-end gap-3 flex-shrink-0">
              <Button variant="outline" onClick={() => { setShowModal(false); setEditingContact(null); }} className="h-12 px-5 text-base">Annuler</Button>
              <Button onClick={handleSave} disabled={saving || !formData.nom.trim()}
                className="bg-indigo-600 hover:bg-indigo-700 text-white h-12 px-8 text-base font-semibold" data-testid="save-contact-btn">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Enregistrement...</> : (editingContact ? 'Modifier' : 'Créer')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Import Preview Modal */}
      {showImportPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[85vh] overflow-hidden shadow-2xl flex flex-col">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Prévisualisation de l'import</h2>
                <p className="text-sm text-slate-500">{importFile?.name} - {importPreviewData.length > 1 ? importPreviewData.length - 1 : 0} premières lignes</p>
              </div>
              <button onClick={() => { setShowImportPreview(false); setImportFile(null); setImportPreviewData([]); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-auto flex-1 p-5">
              <div className="text-xs text-slate-500 mb-3 p-2 bg-blue-50 rounded border border-blue-200">
                Format attendu : <strong>Nom;Prénom;Entreprise;Email;Téléphone;Type;Métier;Corps de métier;Région;Notes</strong>
                <br />Types valides : client, artisan, fournisseur, organisme, personnel, autre
              </div>
              {importPreviewData.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-100">
                        {(importPreviewData[0] || []).map((h, i) => (
                          <th key={i} className="px-2 py-1.5 text-left font-medium text-slate-600 border-b">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {importPreviewData.slice(1, 11).map((row, ri) => (
                        <tr key={ri} className="hover:bg-slate-50">
                          {row.map((cell, ci) => (
                            <td key={ci} className="px-2 py-1.5 text-slate-700 border-b border-slate-100 truncate max-w-[150px]">{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setShowImportPreview(false); setImportFile(null); }}>Annuler</Button>
              <Button onClick={handleConfirmImport} disabled={importing}
                className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="confirm-import-btn">
                {importing ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Import en cours...</> : 'Confirmer l\'import'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsPage;
